import { isNil } from "lodash";

import {
  NotificationDeliveryState,
  NotificationEngineMode,
} from "../../generated/graphql";

/**
 * The cutover facts this module reasons about, narrowed to what the helpers
 * need so they can be exercised without a full GraphQL row.
 */
export type CutoverModeFacts = {
  mode: NotificationEngineMode;
  fullEligible: boolean;
  fullBlockedBy?: string | null;
  fullEligibleBecause?: string | null;
  /**
   * The event that must itself be Full before this one may be, when the write
   * path imposes that precondition. Null for every event that has none.
   */
  requiredPartnerEvent?: string | null;
  /**
   * The partner event's own mode, read from the same page. Undefined when there
   * is no partner, or when the partner's row is not in the list, in which case
   * the precondition is not enforced here and the write path still reports it.
   */
  requiredPartnerMode?: NotificationEngineMode | null;
};

/**
 * The facts the delivery verdict reads: the event's static state plus the live
 * row that can override it.
 *
 * Kept separate from {@link CutoverModeFacts} because the two answer different
 * questions. That one is about what the operator may select; this one is about
 * what is actually happening.
 */
export type DeliveryVerdictFacts = {
  deliveryState: NotificationDeliveryState;
  mode: NotificationEngineMode;
  fullEligible: boolean;
};

/** One entry of the mode dropdown, with the reason it may be unselectable. */
export type ModeOption = {
  value: NotificationEngineMode;
  label: string;
  disabled: boolean;
};

/**
 * Turns a SCREAMING_SNAKE enum value into something readable in a table cell.
 * The GraphQL enums arrive as their raw wire values (`TEXT_MESSAGE`,
 * `ORDER_CONTACT`), which are fine as identifiers and poor as prose.
 */
export const humanizeEnumValue = (value: string): string => {
  const words = value.toLowerCase().split("_").join(" ");
  return words.charAt(0).toUpperCase() + words.slice(1);
};

/**
 * The ticket clause appended wherever an unavailable Full is named. Empty when
 * no ticket is recorded, so the sentence around it still reads.
 */
const blockedByClause = (fullBlockedBy?: string | null): string =>
  isNil(fullBlockedBy) ? "" : `, blocked by ${fullBlockedBy}`;

/**
 * Whether the write path would refuse Full because the event's partner is not
 * itself Full.
 *
 * `setMode` refuses Full on two independent grounds, ineligibility and an
 * un-Full partner event. Disabling only the first leaves the second as a raw
 * server exception after the operator has already chosen Full, which is the
 * failure this page exists to remove.
 */
const partnerBlocksFull = (facts: CutoverModeFacts): boolean =>
  !isNil(facts.requiredPartnerEvent) &&
  !isNil(facts.requiredPartnerMode) &&
  facts.requiredPartnerMode !== NotificationEngineMode.Full;

/**
 * The mode dropdown for one event, with FULL disabled when the write path would
 * refuse it.
 *
 * Offering a FULL that `setMode` rejects turns a clear gate into a raw server
 * error in an alert, and the operator's next move (hunting for a double send,
 * or disabling a legacy path to stop one) is worse than the flip they attempted.
 * The blocking ticket rides in the label so the option explains itself while it
 * is unselectable.
 */
export const modeOptions = (facts: CutoverModeFacts): ModeOption[] => {
  const partnerBlocked = partnerBlocksFull(facts);
  const fullLabel = (): string => {
    if (!facts.fullEligible) {
      return `Full unavailable${blockedByClause(facts.fullBlockedBy)}`;
    }
    return partnerBlocked
      ? `Full unavailable until ${facts.requiredPartnerEvent} is Full`
      : "Full (engine delivers)";
  };
  return [
    {
      value: NotificationEngineMode.Off,
      label: "Off (legacy path only)",
      disabled: false,
    },
    {
      value: NotificationEngineMode.Shadow,
      label: "Shadow (compute + compare, no send)",
      disabled: false,
    },
    {
      value: NotificationEngineMode.Full,
      label: fullLabel(),
      // A stored FULL on an ineligible event still has to render as the selected
      // value, so the option stays present and is only made unselectable.
      disabled: !facts.fullEligible || partnerBlocked,
    },
  ];
};

/**
 * Whether this row reads FULL while the engine is actually running it as
 * SHADOW, because the event is not FULL-eligible.
 *
 * The state a stored value can reach without passing through the write-time
 * gate: a row written straight to the database, or written before the gate
 * existed. Showing `mode` alone in that state tells an operator a cutover
 * happened when it did not.
 */
export const isInertFull = (facts: CutoverModeFacts): boolean =>
  facts.mode === NotificationEngineMode.Full && !facts.fullEligible;

/**
 * The warning shown on a row stored as Full that the engine runs as Shadow.
 *
 * Whether a legacy path is still sending is read from the event's delivery
 * state rather than assumed. An engine-only event such as loaded-on-truck has
 * no legacy sender at all, so claiming one would be the same overstatement this
 * page exists to remove.
 */
export const inertFullWarningText = (
  facts: CutoverModeFacts,
  deliveryState: NotificationDeliveryState,
): string => {
  const consequence =
    deliveryState === NotificationDeliveryState.Delivering
      ? "the legacy path is still delivering"
      : "nothing is delivering for this event";
  return `This event is stored as Full but is not Full-eligible${blockedByClause(
    facts.fullBlockedBy,
  )}, so the engine runs it as Shadow and ${consequence}. Setting Full here did not cut anything over.`;
};

/** Whether Full can be selected for this event, and what stands in the way. */
export const fullAvailabilityText = (facts: CutoverModeFacts): string => {
  if (!facts.fullEligible) {
    return `Full is not available${blockedByClause(
      facts.fullBlockedBy,
    )}. The mode cannot be set to Full until that lands.`;
  }
  return isNil(facts.fullEligibleBecause)
    ? "Full is available."
    : `Full is available. ${facts.fullEligibleBecause}`;
};

/**
 * The delivery verdict actually shown, after the live row is taken into
 * account.
 *
 * `deliveryState` on the registry is a STATIC fact: it describes the event
 * independently of this environment's cutover row, which is what makes it
 * reusable across environments. Rendering it raw meant a dormant event that an
 * operator had just flipped to Full kept reporting "Dormant until Full" while
 * the engine was already delivering, so the page contradicted the flip it had
 * itself accepted.
 *
 * Only dormancy can be lifted this way. `REACHES_NOBODY` means nothing lands
 * even at Full, so a flip does not change it, and `DELIVERING` is already true
 * at every mode.
 *
 * @param facts The event's static state plus the mode and eligibility of its row.
 */
export const effectiveDeliveryState = (
  facts: DeliveryVerdictFacts,
): NotificationDeliveryState => {
  const engineIsDelivering =
    facts.mode === NotificationEngineMode.Full && facts.fullEligible;
  return facts.deliveryState === NotificationDeliveryState.DormantUntilFull &&
    engineIsDelivering
    ? NotificationDeliveryState.Delivering
    : facts.deliveryState;
};

/**
 * The reason text beside the verdict, extended when the live row has overridden
 * the static state so the two cannot read as contradicting each other.
 *
 * @param facts The event's static state plus the mode and eligibility of its row.
 * @param staticReason The registry's reason for the static state.
 */
export const deliveryReasonText = (
  facts: DeliveryVerdictFacts,
  staticReason: string,
): string =>
  effectiveDeliveryState(facts) === facts.deliveryState
    ? staticReason
    : `${staticReason} This row is now set to Full and the event is Full-eligible, so the engine is delivering it and the dormancy above no longer applies.`;

/**
 * The short delivery verdict shown next to an event, separating what the event
 * is INTENDED to do from what anything actually does today.
 *
 * Three states rather than a boolean because an engine-only event that ships
 * dormant is neither delivering nor unreachable: nothing goes out today, and a
 * single flip of this row starts real customer delivery. Labelling that
 * "Delivering today" claimed sends that were not happening.
 */
export const deliveryLabel = (state: NotificationDeliveryState): string => {
  switch (state) {
    case NotificationDeliveryState.Delivering: {
      return "Delivering today";
    }
    case NotificationDeliveryState.DormantUntilFull: {
      return "Dormant until Full";
    }
    case NotificationDeliveryState.ReachesNobody: {
      return "Reaches nobody today";
    }
    default: {
      return "Reaches nobody today";
    }
  }
};

/** Chip colours matching {@link deliveryLabel}: a dormant row is a warning. */
export const deliveryChipColor = (
  state: NotificationDeliveryState,
): "success" | "warning" | "default" => {
  switch (state) {
    case NotificationDeliveryState.Delivering: {
      return "success";
    }
    case NotificationDeliveryState.DormantUntilFull: {
      return "warning";
    }
    case NotificationDeliveryState.ReachesNobody: {
      return "default";
    }
    default: {
      return "default";
    }
  }
};
